/**
 * Unified Plausible Analytics module.
 * Works across web, iOS, and Android — auto-detects platform.
 *
 * Web: loads the Plausible JS snippet via a proxied path (/pa/script.js)
 * Native: sends events directly via the Events API (/pa/event)
 */

import { getPlatform, isNative } from './native';

const PLAUSIBLE_DOMAIN = 'word-labyrinth.com';
const SCRIPT_URL = '/pa/script.js';
const EVENT_URL = '/pa/event';

// For native apps, we need the full URL since relative paths resolve to the server
const getEventEndpoint = () => {
    if (isNative()) {
        const base = process.env.REACT_APP_API_URL || 'https://www.word-labyrinth.com';
        return `${base}${EVENT_URL}`;
    }
    return EVENT_URL;
};

let plausibleReady = false;
const eventQueue = [];

function flushQueue() {
    while (eventQueue.length > 0) {
        const { name, options } = eventQueue.shift();
        sendEvent(name, options);
    }
}

function sendEvent(name, options = {}) {
    // Always attach platform
    const props = { ...options.props, platform: getPlatform() };

    if (isNative()) {
        // Native: use Events API directly (no JS snippet in WebView)
        const payload = {
            name,
            url: window.location.href,
            domain: PLAUSIBLE_DOMAIN,
            props,
        };
        if (options.revenue) payload.revenue = options.revenue;

        fetch(getEventEndpoint(), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        }).catch(() => {});
        return;
    }

    // Web: use the loaded window.plausible function
    if (typeof window.plausible === 'function') {
        const eventData = { props };
        if (options.revenue) eventData.revenue = options.revenue;
        if (options.u) eventData.u = options.u;
        window.plausible(name, eventData);
    }
}

/**
 * Initialize Plausible analytics. Call once at app startup.
 */
export function initPlausible() {
    if (isNative()) {
        // Native doesn't need a script — events go via fetch
        plausibleReady = true;
        flushQueue();
        return;
    }

    // Web: load the Plausible script (proxied to avoid ad blockers)
    const script = document.createElement('script');
    script.defer = true;
    script.dataset.domain = PLAUSIBLE_DOMAIN;
    script.dataset.api = EVENT_URL;
    script.src = SCRIPT_URL;
    script.onload = () => {
        plausibleReady = true;
        flushQueue();
    };
    script.onerror = () => {
        // Script blocked by ad blocker — fall back to direct fetch
        window.plausible = (name, options = {}) => {
            const payload = {
                name,
                url: options.u || window.location.href,
                domain: PLAUSIBLE_DOMAIN,
                referrer: document.referrer || null,
                props: options.props || {},
            };
            if (options.revenue) payload.revenue = options.revenue;
            fetch(EVENT_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            }).catch(() => {});
        };
        plausibleReady = true;
        flushQueue();
    };
    document.head.appendChild(script);
}

/**
 * Track a pageview (for SPA route changes).
 */
export function trackPageview(url) {
    const options = { props: {}, u: url };
    if (!plausibleReady) {
        eventQueue.push({ name: 'pageview', options });
        return;
    }
    sendEvent('pageview', options);
}

/**
 * Track a custom event.
 * @param {string} name - Event name (e.g. 'Game Complete', 'Purchase')
 * @param {Object} [props] - Custom properties
 * @param {Object} [revenue] - Revenue data { currency: 'EUR', amount: 1.99 }
 */
export function trackEvent(name, props = {}, revenue = null) {
    const options = { props };
    if (revenue) options.revenue = revenue;

    if (!plausibleReady) {
        eventQueue.push({ name, options });
        return;
    }
    sendEvent(name, options);
}
